import Layout from "src/router/AuthenticatedRouter/Layout";
import { useNavigate, useSearchParams } from "react-router-dom";
import Button from "src/components/Button";
import { ReactComponent as Plus } from "src/assets/svg/+.svg";
import { ReactComponent as Refresh } from "src/assets/svg/refresh.svg";
import Tabs from "src/components/Tabs";
import Job from "src/types/models/Job";
import React, { useEffect, useState } from "react";
import { getJob } from "src/network/job";
import JobDetail from "./tabs/JobDetail";
import LoaderPage from "src/components/Loader/LoaderPage";
import Skills from "./tabs/Skills";
import { getInterviewMetrics } from "src/network/interview";
import { InterviewMetrics } from "src/network/interview/type";
import useUpdate from "./hook/useUpdate";
import Settings from "./tabs/Settings";
import EditButton from "src/components/EditButton";
import useSlugParam from "src/hooks/useSlug";
import RemindAllButton from "../components/RemindAllButton";
import AppliedTalents from "./tabs/AppliedTalents";
import Interviews from "./tabs/Interviews";
import AtsTalents from "./tabs/AtsTalents";
import useLoader from "src/hooks/useLoader";
import { syncJobApplications } from "src/network/company_ats";

export type JobPageTab =
  | "Interviews"
  | "Applied Talents"
  | "ATS Talents"
  | "Job Description"
  | "Required Skills"
  | "Settings";

const tabs: JobPageTab[] = [
  "Interviews",
  "Applied Talents",
  "ATS Talents",
  "Job Description",
  "Required Skills",
  "Settings"
];

const JobDetailPage = () => {
  const navigate = useNavigate();
  const { id, slug } = useSlugParam();
  // We will use this seed to remount the component if neccesary
  const [seed, setSeed] = useState(0);
  const [job, setJob] = useState<Job>();
  const [params] = useSearchParams();
  const { update } = useUpdate(setJob);
  const tab = params.get("tab") || tabs[0];
  const [metrics, setMetrics] = useState<InterviewMetrics>();
  // Routing to sync ats candidates
  const { ldng: syncAts, loading: syncingAts } = useLoader(syncJobApplications, {
    errorMsg: "Error syncing candidates",
    successCallback: () => setSeed(seed + 1)
  });

  // Initial Fetch
  useEffect(() => {
    if (!id) return;
    (async () => {
      const job = await getJob({ id: Number(id) });
      setJob(job);
      const m = await getInterviewMetrics({ job_id: Number(id) });
      setMetrics(m);
    })();
  }, [id]);

  const InviteButton = job?.active ? (
    <Button onClick={() => navigate("/job/invite/" + slug)} shapeVariant={2} variant="white_blue">
      <Plus className="mr-[10px] h-6 w-6" /> Invite Talent
    </Button>
  ) : undefined;
  let rightChildren;
  switch (tab) {
    case tabs[0]:
      rightChildren = (
        <div className="flex gap-9">
          <RemindAllButton
            disabled={!job?.active || !metrics?.INVITED}
            job_id={id}
            onRemind={() => setSeed(seed + 1)}
          />
          {InviteButton}
        </div>
      );

      break;
    case tabs[1]:
      break;
    case tabs[2]:
      if (!job) break;
      rightChildren = (
        <Button
          className="min-w-[259px] h-[56px]"
          onClick={() => syncAts(job.id)}
          disabled={!job.ATS?.ats_company_id}
          loading={syncingAts}
          shapeVariant={2}
          variant="white_blue"
        >
          <Refresh className="w-6 h-6 mr-[10px]" /> Sync new candidates
        </Button>
      );
      break;
    default:
      rightChildren = <EditButton form="update_job" />;
      break;
  }

  let title: React.ReactNode = "Loading...";
  if (job)
    title = (
      <p className="flex gap-6 items-center">
        {job.job_title} {!job.active && <InactiveBadge />}
      </p>
    );

  return (
    <Layout
      key={seed}
      title={title}
      className={`flex flex-col ${[tabs[0], tabs[1]].includes(tab as JobPageTab) && "!overflow-hidden"}  `}
      back="/"
      rightChildren={rightChildren}
    >
      {job ? (
        <Tabs
          className="mb-6"
          tabs={tabs}
          withSearchParam
          contents={[
            <Interviews metrics={metrics} job={job} />,
            <AppliedTalents metrics={metrics} />,
            <AtsTalents metrics={metrics} />,
            <JobDetail onUpdate={update} job={job} />,
            <Skills onUpdate={update} job={job} />,
            <Settings onUpdate={update} job={job} />
          ]}
        />
      ) : (
        <LoaderPage />
      )}
    </Layout>
  );
};

const InactiveBadge = () => (
  <p className="font-poppins text-sm text-error font-semibold p-2 border-[1px] border-error  rounded-lg">
    Inactive
  </p>
);

export default JobDetailPage;
